import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  getTriggerSpec,
  NUMERIC_OPERATOR_SYMBOL,
  TAG_NUMERIC_OPERATORS,
  TAG_RULE_OPERATORS,
  TAG_RULE_SPEAKERS,
  TRIGGER_TYPE_OPTIONS,
} from 'components/@home/@settings/Tabs/Agents/tagPanels/triggerSpecs';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import { arrayOf, func, object, string } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';

const fieldLabelSx = {
  fontSize: theme => theme.typography.pxToRem(12),
  color: 'text.secondary',
  whiteSpace: 'nowrap',
};

const fieldRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const fieldColSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
};

const renderTriggerOption = option => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Typography variant="body2">{I18n.t(`AgenticChatFlow.${option.labelKey}`)}</Typography>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
      }}
    >
      {I18n.t(`AgenticChatFlow.${option.descriptionKey}`)}
    </Typography>
  </Box>
);

const ToolField = ({ rule, availableTools, onChange }) => {
  const value = availableTools.find(t => t.name === rule.toolName) || null;
  return (
    <Box sx={fieldRowSx}>
      <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldTool')}</Typography>
      <Autocomplete
        size="small"
        fullWidth
        options={availableTools}
        getOptionLabel={opt => opt?.name || ''}
        value={value}
        onChange={(_, next) => onChange({ ...rule, toolName: next?.name || '' })}
        renderInput={params => (
          <TextField {...params} placeholder={I18n.t('AgenticChatFlow.TagsRuleToolPlaceholder')} />
        )}
      />
    </Box>
  );
};
ToolField.propTypes = {
  rule: object.isRequired,
  availableTools: arrayOf(object).isRequired,
  onChange: func.isRequired,
};

const SkillField = ({ rule, availableSkills, onChange }) => {
  const value = availableSkills.find(s => s.name === rule.skillName) || null;
  return (
    <Box sx={fieldRowSx}>
      <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldSkill')}</Typography>
      <Autocomplete
        size="small"
        fullWidth
        options={availableSkills}
        getOptionLabel={opt => opt?.displayName || opt?.name || ''}
        value={value}
        onChange={(_, next) => onChange({ ...rule, skillName: next?.name || '' })}
        renderInput={params => (
          <TextField {...params} placeholder={I18n.t('AgenticChatFlow.TagsRuleSkillPlaceholder')} />
        )}
      />
    </Box>
  );
};
SkillField.propTypes = {
  rule: object.isRequired,
  availableSkills: arrayOf(object).isRequired,
  onChange: func.isRequired,
};

const SpeakerField = ({ rule, onChange }) => (
  <Box sx={fieldRowSx}>
    <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldSpeaker')}</Typography>
    <Select
      size="small"
      fullWidth
      displayEmpty
      value={rule.speaker || ''}
      onChange={e => onChange({ ...rule, speaker: e.target.value || undefined })}
    >
      <MenuItem value="">
        <em>{I18n.t('AgenticChatFlow.TagsRuleSpeakerPlaceholder')}</em>
      </MenuItem>
      {TAG_RULE_SPEAKERS.map(s => (
        <MenuItem key={s} value={s}>
          {I18n.t(`AgenticChatFlow.TagsSpeaker_${s}`)}
        </MenuItem>
      ))}
    </Select>
  </Box>
);
SpeakerField.propTypes = { rule: object.isRequired, onChange: func.isRequired };

const ThresholdField = ({ rule, unit = 'count', onChange }) => {
  const threshold = rule.threshold || { operator: 'gt', value: '' };
  return (
    <Box sx={fieldRowSx}>
      <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldThreshold')}</Typography>
      <Select
        size="small"
        sx={{ width: 72 }}
        value={threshold.operator || 'gt'}
        onChange={e => onChange({ ...rule, threshold: { ...threshold, operator: e.target.value } })}
      >
        {TAG_NUMERIC_OPERATORS.map(op => (
          <MenuItem key={op} value={op}>
            {NUMERIC_OPERATOR_SYMBOL[op]}
          </MenuItem>
        ))}
      </Select>
      <TextField
        size="small"
        type="number"
        fullWidth
        placeholder={
          unit === 'seconds'
            ? I18n.t('AgenticChatFlow.TagsRuleSecondsPlaceholder')
            : I18n.t('AgenticChatFlow.TagsRuleCountPlaceholder')
        }
        value={threshold.value ?? ''}
        onChange={e => {
          const raw = e.target.value;
          const parsed = raw === '' ? '' : Number(raw);
          onChange({
            ...rule,
            threshold: { ...threshold, value: raw === '' ? undefined : parsed },
          });
        }}
      />
      {unit === 'seconds' && (
        <Typography
          sx={{ fontSize: theme => theme.typography.pxToRem(12.5), color: 'text.secondary' }}
        >
          {I18n.t('AgenticChatFlow.TagsRuleSecondsSuffix')}
        </Typography>
      )}
    </Box>
  );
};
ThresholdField.propTypes = {
  rule: object.isRequired,
  unit: string,
  onChange: func.isRequired,
};

const MetadataField = ({ rule, onChange }) => {
  const meta = rule.metadata || { key: '', operator: 'eq', value: '' };
  return (
    <Box sx={fieldColSx}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <Box sx={fieldRowSx}>
          <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldKey')}</Typography>
          <TextField
            size="small"
            fullWidth
            placeholder={I18n.t('AgenticChatFlow.TagsRuleKeyPlaceholder')}
            value={meta.key || ''}
            onChange={e => onChange({ ...rule, metadata: { ...meta, key: e.target.value } })}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'center' }}>
        <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldOperator')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <Select
            size="small"
            fullWidth
            value={meta.operator || 'eq'}
            onChange={e => onChange({ ...rule, metadata: { ...meta, operator: e.target.value } })}
          >
            {TAG_RULE_OPERATORS.map(op => (
              <MenuItem key={op} value={op}>
                {I18n.t(`AgenticChatFlow.TagsOperator_${op}`)}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            fullWidth
            placeholder={I18n.t('AgenticChatFlow.TagsRuleValuePlaceholder')}
            value={meta.value || ''}
            onChange={e => onChange({ ...rule, metadata: { ...meta, value: e.target.value } })}
          />
        </Box>
      </Box>
    </Box>
  );
};
MetadataField.propTypes = { rule: object.isRequired, onChange: func.isRequired };

const TextValueField = ({ rule, onChange }) => (
  <Box sx={fieldRowSx}>
    <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleFieldText')}</Typography>
    <TextField
      size="small"
      fullWidth
      placeholder={I18n.t('AgenticChatFlow.TagsRuleTextPlaceholder')}
      value={rule.text || ''}
      onChange={e => onChange({ ...rule, text: e.target.value })}
    />
  </Box>
);
TextValueField.propTypes = { rule: object.isRequired, onChange: func.isRequired };

const renderField = (field, props) => {
  switch (field.kind) {
    case 'tool':
      return <ToolField key="tool" {...props} />;
    case 'skill':
      return <SkillField key="skill" {...props} />;
    case 'speaker':
      return <SpeakerField key="speaker" {...props} />;
    case 'threshold':
      return <ThresholdField key="threshold" unit={field.unit} {...props} />;
    case 'metadata':
      return <MetadataField key="metadata" {...props} />;
    case 'text':
      return <TextValueField key="text" {...props} />;
    default:
      return null;
  }
};

const RuleRow = ({ rule, availableTools, availableSkills, onChange, onRemove }) => {
  const spec = getTriggerSpec(rule.triggerType);
  const isMetadata = spec?.fields?.some(f => f.kind === 'metadata');

  const handleTriggerChange = nextType => {
    onChange({ triggerType: nextType });
  };

  return (
    <Box
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: 'minmax(140px, 1fr) minmax(180px, 1.5fr) auto',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
        },
        isMetadata && {
          gridTemplateColumns: 'minmax(140px, 1fr) minmax(220px, 2fr) auto',
        },
      ]}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={fieldLabelSx}>{I18n.t('AgenticChatFlow.TagsRuleTriggerType')}</Typography>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={rule.triggerType || ''}
          onChange={e => handleTriggerChange(e.target.value)}
          renderValue={value => {
            if (!value) return <em>{I18n.t('AgenticChatFlow.TagsRuleTriggerPlaceholder')}</em>;
            const opt = TRIGGER_TYPE_OPTIONS.find(o => o.value === value);
            return opt ? I18n.t(`AgenticChatFlow.${opt.labelKey}`) : value;
          }}
        >
          {TRIGGER_TYPE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {renderTriggerOption(opt)}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={fieldColSx}>
        {spec?.fields?.map(field =>
          renderField(field, { rule, availableTools, availableSkills, onChange }),
        )}
      </Box>
      <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={onRemove}>
        <DeleteOutlineIcon size={18} />
      </IconButton>
    </Box>
  );
};

RuleRow.propTypes = {
  rule: object.isRequired,
  availableTools: arrayOf(object).isRequired,
  availableSkills: arrayOf(object).isRequired,
  onChange: func.isRequired,
  onRemove: func.isRequired,
};

export default RuleRow;
